import { useState, useMemo, useEffect } from "react";
import { useApp } from "../../store.jsx";
import { Icon, Pagination, EmptyState } from "../../components/Common.jsx";
import {
  getWorkingDays,
  updateWorkingDays,
  getHolidays,
  createHoliday,
  deleteHoliday,
  seedHolidays,
  getLeaves,
  markLeave,
  cancelLeave,
  listEmployees,
  listDepartments,
} from "../../lib/api";
import { useRole } from "../../hooks/useRole";

const SCHEDULE_OPTIONS = [
  { value: "mon-fri", label: "Mon – Fri (5 days)" },
  { value: "mon-sat", label: "Mon – Sat (6 days)" },
  { value: "mon-sun", label: "Mon – Sun (7 days)" },
];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function fmtDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function HolidaysPage() {
  const tabs = [
    { id: "workingDays", label: "Working Days", icon: "calendar-clock" },
    { id: "holidays", label: "Holidays", icon: "calendar" },
    { id: "leaves", label: "Leaves", icon: "calendar-x" },
  ];

  const [tab, setTab] = useState(tabs[0].id);

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Holidays & Leaves</h1>
          <p className="page-sub">
            Manage working schedules, holidays, and employee leaves
          </p>
        </div>
      </div>

      {tabs.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`filter-chip ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <Icon name={t.icon} size={14} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "workingDays" && <WorkingDaysTab />}
      {tab === "holidays" && <HolidaysTab />}
      {tab === "leaves" && <LeavesTab />}
    </section>
  );
}

// ─── WORKING DAYS TAB ────────────────────────────────────────
function WorkingDaysTab() {
  const { toast } = useApp();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [wdRes, depts, emps] = await Promise.all([
        getWorkingDays(),
        listDepartments().catch(() => []),
        listEmployees().catch(() => ({ employees: [] })),
      ]);
      setConfig(wdRes || { default: "mon-fri", departments: {}, employees: {} });
      setDepartments(Array.isArray(depts) ? depts : []);
      setEmployees((emps.employees || []).filter((e) => e.role === "Employee"));
    } catch (e) {
      toast(e.message || "Failed to load working days config", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (newConfig) => {
    setSaving(true);
    try {
      await updateWorkingDays(newConfig);
      setConfig(newConfig);
      toast("Working days updated", "success");
    } catch (e) {
      toast(e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card" style={{ padding: 24, textAlign: "center" }}>Loading working days config…</div>;
  }
  if (!config) return null;

  const deptOverrides = config.departments || {};
  const empOverrides = config.employees || {};

  const setDefault = (val) => save({ ...config, default: val });

  const setDeptOverride = (deptName, val) => {
    const next = { ...config, departments: { ...deptOverrides } };
    if (val === config.default) delete next.departments[deptName];
    else next.departments[deptName] = val;
    save(next);
  };

  const setEmpOverride = (userId, val) => {
    const next = { ...config, employees: { ...empOverrides } };
    if (val === "") delete next.employees[userId];
    else next.employees[userId] = val;
    save(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Company default */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>Company Default Schedule</h3>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
          This applies to all employees unless overridden at department or individual level.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          {SCHEDULE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`filter-chip ${config.default === opt.value ? "active" : ""}`}
              onClick={() => setDefault(opt.value)}
              disabled={saving}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Department overrides */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>Department Overrides</h3>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
          Set a different schedule for specific departments. Leave as "Company Default" to inherit.
        </p>
        {departments.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-dim)" }}>No departments found.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {departments.map((d) => (
              <div key={d.id || d.name} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ minWidth: 140, fontSize: 13, fontWeight: 500 }}>{d.name}</span>
                <select
                  className="input"
                  style={{ height: 32, padding: "0 8px", flex: "1 1 150px", minWidth: 0, fontSize: 13 }}
                  value={deptOverrides[d.name] || ""}
                  onChange={(e) => setDeptOverride(d.name, e.target.value)}
                  disabled={saving}
                >
                  <option value="">Company Default ({config.default})</option>
                  {SCHEDULE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {deptOverrides[d.name] && (
                  <button
                    className="icon-btn"
                    title="Reset to default"
                    onClick={() => setDeptOverride(d.name, config.default)}
                    disabled={saving}
                  >
                    <Icon name="x" size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Employee overrides */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>Employee Overrides</h3>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
          Override the schedule for individual employees. This takes priority over department settings.
        </p>
        {Object.keys(empOverrides).length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 10 }}>No employee overrides set.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            {Object.entries(empOverrides).map(([userId, schedule]) => {
              const emp = employees.find((e) => e.id === userId);
              return (
                <div key={userId} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ minWidth: 140, fontSize: 13, fontWeight: 500 }}>
                    {emp ? emp.name : userId}
                  </span>
                  <select
                    className="input"
                    style={{ height: 32, padding: "0 8px", flex: "1 1 150px", minWidth: 0, fontSize: 13 }}
                    value={schedule}
                    onChange={(e) => setEmpOverride(userId, e.target.value)}
                    disabled={saving}
                  >
                    {SCHEDULE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    className="icon-btn"
                    title="Remove override"
                    onClick={() => setEmpOverride(userId, "")}
                    disabled={saving}
                  >
                    <Icon name="trash-2" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <AddEmployeeOverride
          employees={employees}
          existing={empOverrides}
          onAdd={(userId, schedule) => setEmpOverride(userId, schedule)}
          saving={saving}
        />
      </div>
    </div>
  );
}

function AddEmployeeOverride({ employees, existing, onAdd, saving }) {
  const [selectedEmp, setSelectedEmp] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState("mon-sat");
  const available = employees.filter((e) => !existing[e.id]);

  if (available.length === 0) return null;

  const handleAdd = () => {
    if (!selectedEmp) return;
    onAdd(selectedEmp, selectedSchedule);
    setSelectedEmp("");
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <select
        className="input"
        style={{ height: 32, padding: "0 8px", flex: "1 1 150px", minWidth: 0, fontSize: 13 }}
        value={selectedEmp}
        onChange={(e) => setSelectedEmp(e.target.value)}
        disabled={saving}
      >
        <option value="">Select employee…</option>
        {available.map((e) => (
          <option key={e.id} value={e.id}>{e.name}</option>
        ))}
      </select>
      <select
        className="input"
        style={{ height: 32, padding: "0 8px", flex: "1 1 150px", minWidth: 0, fontSize: 13 }}
        value={selectedSchedule}
        onChange={(e) => setSelectedSchedule(e.target.value)}
        disabled={saving}
      >
        {SCHEDULE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <button
        className="btn btn-primary btn-sm"
        onClick={handleAdd}
        disabled={!selectedEmp || saving}
      >
        <Icon name="plus" size={14} /> Add
      </button>
    </div>
  );
}

// ─── HOLIDAYS TAB ────────────────────────────────────────────
function HolidaysTab() {
  const { toast, openModal, closeModal } = useApp();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [seeding, setSeeding] = useState(false);
  const [view, setView] = useState("calendar");

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await getHolidays(year);
      setHolidays(Array.isArray(res) ? res : []);
    } catch (e) {
      toast(e.message || "Failed to load holidays", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach((h) => {
      const dt = new Date(h.date);
      const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
      m[key] = h;
    });
    return m;
  }, [holidays]);

  const sorted = useMemo(() => {
    return [...holidays].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [holidays]);

  const now = new Date();
  const upcoming = holidays.filter((h) => new Date(h.date) >= now).length;
  const past = holidays.filter((h) => new Date(h.date) < now).length;

  const onSeed = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      await seedHolidays(year);
      toast("National holidays seeded", "success");
      await refresh();
    } catch (e) {
      toast(e.message || "Seed failed", "error");
    } finally {
      setSeeding(false);
    }
  };

  const openAddModalForDate = (prefillDate) => {
    const data = { name: "", date: prefillDate || "", type: "custom" };

    const save = async () => {
      if (!data.name || !data.date) {
        toast("Name and date are required", "error");
        return;
      }
      try {
        await createHoliday({ name: data.name, date: data.date, type: data.type });
        toast("Holiday added", "success");
        closeModal();
        await refresh();
      } catch (e) {
        toast(e.message || "Failed to add holiday", "error");
      }
    };

    openModal(
      <>
        <div className="modal-head">
          <h3>Add Holiday</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Holiday Name</label>
              <input type="text" className="input" placeholder="e.g. Diwali" autoFocus onChange={(e) => (data.name = e.target.value)} />
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" className="input" defaultValue={data.date} onChange={(e) => (data.date = e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Type</label>
              <select className="input" defaultValue="custom" onChange={(e) => (data.type = e.target.value)}>
                <option value="custom">Custom</option>
                <option value="national">National</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>
            <Icon name="check" size={16} /> Add Holiday
          </button>
        </div>
      </>,
      "md",
    );
  };

  const onDelete = (h) => {
    const dateStr = new Date(h.date).toLocaleDateString("en-IN", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
    const confirmDelete = async () => {
      try {
        await deleteHoliday(h.id);
        toast("Holiday deleted", "success");
        closeModal();
        await refresh();
      } catch (e) {
        toast(e.message || "Delete failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Delete Holiday</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to delete this holiday?</p>
          <div style={{ background: "var(--surface)", borderRadius: 8, padding: 12, margin: "12px 0" }}>
            <strong>{h.name}</strong>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{dateStr}</div>
          </div>
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>All employees and managers will be notified that this holiday has been cancelled.</p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger" onClick={confirmDelete}>
            <Icon name="trash-2" size={16} /> Delete Holiday
          </button>
        </div>
      </>,
      "md",
    );
  };

  const onCalendarDayClick = (dateStr) => {
    const existing = holidayMap[dateStr];
    if (existing) {
      onDelete(existing);
    } else {
      openAddModalForDate(dateStr);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {holidays.length} holidays ({upcoming} upcoming, {past} past)
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              className={`filter-chip ${view === "calendar" ? "active" : ""}`}
              onClick={() => setView("calendar")}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <Icon name="calendar" size={13} /> Calendar
            </button>
            <button
              className={`filter-chip ${view === "list" ? "active" : ""}`}
              onClick={() => setView("list")}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <Icon name="list" size={13} /> List
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="icon-btn" onClick={() => setYear(year - 1)} title="Previous year">
            <Icon name="chevron-left" size={16} />
          </button>
          <select
            className="input"
            style={{ height: 30, padding: "0 8px", width: 80, fontSize: 13, textAlign: "center" }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button className="icon-btn" onClick={() => setYear(year + 1)} title="Next year">
            <Icon name="chevron-right" size={16} />
          </button>
          <div style={{ width: 1, height: 20, background: "var(--border)", margin: "0 4px" }} />
          <button className="btn btn-ghost btn-sm" onClick={onSeed} disabled={seeding}>
            <Icon name="zap" size={14} /> {seeding ? "Seeding…" : "Seed National"}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => openAddModalForDate("")}>
            <Icon name="plus" size={16} /> Add Holiday
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: "center" }}>Loading holidays…</div>
      ) : view === "calendar" ? (
        <>
          {/* Legend */}
          <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 12, color: "var(--text-muted)" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--info)", display: "inline-block" }} /> National Holiday
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--primary)", display: "inline-block" }} /> Custom Holiday
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--surface-3)", display: "inline-block" }} /> Today
            </span>
            <span>Click a date to add/remove holiday</span>
          </div>

          {/* 12-month calendar grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 14,
          }}>
            {Array.from({ length: 12 }, (_, i) => (
              <MonthCalendar
                key={i}
                year={year}
                month={i}
                holidayMap={holidayMap}
                onDayClick={onCalendarDayClick}
              />
            ))}
          </div>

          {/* Upcoming holidays list below calendar */}
          {sorted.length > 0 && (
            <div className="card" style={{ marginTop: 20, padding: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>All Holidays — {year}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {sorted.map((h) => {
                  const dt = new Date(h.date);
                  const dayName = dt.toLocaleDateString("en-IN", { weekday: "short" });
                  const isPast = dt < now;
                  return (
                    <div
                      key={h.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 10px",
                        borderRadius: 6,
                        background: "var(--surface-2)",
                        opacity: isPast ? 0.5 : 1,
                      }}
                    >
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: h.type === "national" ? "var(--info)" : "var(--primary)",
                        flexShrink: 0,
                      }} />
                      <span style={{ fontSize: 13, fontWeight: 500, minWidth: 160 }}>{h.name}</span>
                      <span style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 100 }}>{fmtDate(h.date)}</span>
                      <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{dayName}</span>
                      <span className={`badge ${h.type === "national" ? "badge-info" : "badge-muted"}`} style={{ fontSize: 10, marginLeft: "auto" }}>
                        {h.type === "national" ? "National" : "Custom"}
                      </span>
                      <button className="icon-btn" onClick={() => onDelete(h)} title="Delete" style={{ flexShrink: 0 }}>
                        <Icon name="trash-2" size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        /* List view */
        sorted.length ? (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Holiday</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((h) => {
                  const dt = new Date(h.date);
                  const dayName = dt.toLocaleDateString("en-IN", { weekday: "long" });
                  const isPast = dt < now;
                  return (
                    <tr key={h.id}>
                      <td data-label="Holiday" style={{ fontWeight: 500 }}>{h.name}</td>
                      <td data-label="Date">{fmtDate(h.date)}</td>
                      <td data-label="Day" style={{ color: "var(--text-muted)" }}>{dayName}</td>
                      <td data-label="Type">
                        <span className={`badge ${h.type === "national" ? "badge-info" : "badge-muted"}`}>
                          {h.type === "national" ? "National" : "Custom"}
                        </span>
                      </td>
                      <td data-label="Status">
                        {isPast ? (
                          <span className="badge badge-muted">Past</span>
                        ) : (
                          <span className="badge badge-success">Upcoming</span>
                        )}
                      </td>
                      <td data-label="">
                        <div className="row-actions">
                          <button className="icon-btn" onClick={() => onDelete(h)} title="Delete">
                            <Icon name="trash-2" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No holidays found"
            desc="Add holidays or seed national holidays to get started."
            icon="calendar"
            cta="Add Holiday"
            onCta={() => openAddModalForDate("")}
          />
        )
      )}
    </>
  );
}

// ─── MONTH CALENDAR COMPONENT ────────────────────────────────
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function MonthCalendar({ year, month, holidayMap, onDayClick }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, textAlign: "center" }}>
        {MONTHS[month]} {year}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center" }}>
        {DAY_HEADERS.map((d) => (
          <div key={d} style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 600, padding: "2px 0" }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const holiday = holidayMap[dateStr];
          const isToday = dateStr === todayStr;
          const isSunday = (i % 7) === 0;

          let bg = "transparent";
          let color = "var(--text)";
          let fontWeight = 400;
          let border = "2px solid transparent";
          let title = "";

          if (holiday) {
            bg = holiday.type === "national" ? "var(--info)" : "var(--primary)";
            color = "#fff";
            fontWeight = 600;
            title = `${holiday.name} (${holiday.type}) — click to remove`;
          } else {
            title = "Click to add holiday";
          }

          if (isToday && !holiday) {
            border = "2px solid var(--primary)";
            fontWeight = 700;
          }

          if (isSunday && !holiday) {
            color = "var(--error)";
          }

          return (
            <div
              key={dateStr}
              onClick={() => onDayClick(dateStr)}
              title={title}
              style={{
                fontSize: 12,
                padding: "4px 2px",
                borderRadius: 4,
                cursor: "pointer",
                background: bg,
                color,
                fontWeight,
                border,
                lineHeight: "18px",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!holiday) e.target.style.background = "var(--surface-3)"; }}
              onMouseLeave={(e) => { if (!holiday) e.target.style.background = "transparent"; }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── LEAVES TAB ──────────────────────────────────────────────
function LeavesTab() {
  const { toast, openModal, closeModal } = useApp();
  useRole();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const [leavesRes, empsRes] = await Promise.all([
        getLeaves(params),
        listEmployees().catch(() => ({ employees: [] })),
      ]);
      setLeaves(Array.isArray(leavesRes) ? leavesRes : []);
      setEmployees((empsRes.employees || []).filter((e) => e.role === "Employee"));
    } catch (e) {
      toast(e.message || "Failed to load leaves", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const empMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);

  const rows = useMemo(() => {
    let r = [...leaves];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((l) => {
        const name = l.user?.name || empMap[l.userId]?.name || "";
        return (
          name.toLowerCase().includes(q) ||
          (l.reason || "").toLowerCase().includes(q)
        );
      });
    }
    if (filter === "whatsapp") r = r.filter((l) => l.source === "whatsapp");
    else if (filter === "web") r = r.filter((l) => l.source === "web");
    r.sort((a, b) => new Date(b.date) - new Date(a.date));
    return r;
  }, [leaves, search, filter, empMap]);

  const total = rows.length;
  const start = (page - 1) * pageSize;
  const paged = rows.slice(start, start + pageSize);
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const openMarkLeaveModal = () => {
    const data = { userId: "", date: "", reason: "" };

    const save = async () => {
      if (!data.userId || !data.date) {
        toast("Employee and date are required", "error");
        return;
      }
      try {
        await markLeave({ userId: data.userId, date: data.date, reason: data.reason || undefined });
        toast("Leave marked", "success");
        closeModal();
        refresh();
      } catch (e) {
        toast(e.message || "Failed to mark leave", "error");
      }
    };

    openModal(
      <>
        <div className="modal-head">
          <h3>Mark Leave</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Employee</label>
              <select className="input" onChange={(e) => (data.userId = e.target.value)}>
                <option value="">Select employee…</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" className="input" onChange={(e) => (data.date = e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Reason (optional)</label>
              <input type="text" className="input" placeholder="e.g. Sick leave, Personal" onChange={(e) => (data.reason = e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>
            <Icon name="check" size={16} /> Mark Leave
          </button>
        </div>
      </>,
      "md",
    );
  };

  const onCancel = async (l) => {
    const empName = l.user?.name || empMap[l.userId]?.name || "this employee";
    if (!window.confirm(`Cancel leave for ${empName} on ${fmtDate(l.date)}?`)) return;
    try {
      await cancelLeave(l.id);
      toast("Leave cancelled", "success");
      refresh();
    } catch (e) {
      toast(e.message || "Cancel failed", "error");
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {total} leave record{total !== 1 ? "s" : ""}
        </span>
        <button className="btn btn-primary btn-sm" onClick={openMarkLeaveModal}>
          <Icon name="plus" size={16} /> Mark Leave
        </button>
      </div>

      <div className="table-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-search">
            <Icon name="search" size={14} />
            <input type="text" placeholder="Search by name or reason…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          {[
            ["all", "All"],
            ["whatsapp", "WhatsApp"],
            ["web", "Web"],
          ].map(([k, l]) => (
            <button key={k} className={`filter-chip ${filter === k ? "active" : ""}`} onClick={() => { setFilter(k); setPage(1); }}>
              {l}
            </button>
          ))}
          <input
            type="date"
            className="input"
            style={{ height: 30, padding: "0 8px", width: 140, fontSize: 13 }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From date"
            placeholder="From"
          />
          <input
            type="date"
            className="input"
            style={{ height: 30, padding: "0 8px", width: 140, fontSize: 13 }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="To date"
            placeholder="To"
          />
          {(dateFrom || dateTo) && (
            <button className="icon-btn" title="Clear dates" onClick={() => { setDateFrom(""); setDateTo(""); }}>
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: "center" }}>Loading leaves…</div>
      ) : paged.length ? (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Date</th>
                <th>Day</th>
                <th>Reason</th>
                <th>Source</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((l) => {
                const empName = l.user?.name || empMap[l.userId]?.name || l.userId;
                const dt = new Date(l.date);
                const dayName = dt.toLocaleDateString("en-IN", { weekday: "short" });
                return (
                  <tr key={l.id}>
                    <td data-label="Employee" style={{ fontWeight: 500 }}>{empName}</td>
                    <td data-label="Date">{fmtDate(l.date)}</td>
                    <td data-label="Day" style={{ color: "var(--text-muted)" }}>{dayName}</td>
                    <td data-label="Reason" style={{ color: l.reason ? "var(--text)" : "var(--text-dim)" }}>
                      {l.reason || "—"}
                    </td>
                    <td data-label="Source">
                      <span className={`badge ${l.source === "whatsapp" ? "badge-success" : "badge-info"}`}>
                        {l.source === "whatsapp" ? "WhatsApp" : "Web"}
                      </span>
                    </td>
                    <td data-label="">
                      <div className="row-actions">
                        <button className="icon-btn" onClick={() => onCancel(l)} title="Cancel leave">
                          <Icon name="trash-2" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            pages={pages}
            total={total}
            from={start}
            to={Math.min(start + pageSize, total)}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      ) : (
        <EmptyState
          title="No leaves found"
          desc={leaves.length ? "No leaves match the current filter." : "No leaves have been recorded yet."}
          icon="calendar-x"
          cta="Mark Leave"
          onCta={openMarkLeaveModal}
        />
      )}
    </>
  );
}
